import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { apiService, API_BASE_URL } from '../../services/api';
import { formatAmsterdamDate } from '../../utils/formatAmsterdamDate';
import StarRating from '../../components/common/StarRating';
import Avatar from '../../components/common/Avatar';
import ReviewCard from '../../components/portal/ReviewCard';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem var(--desktop-page-padding-x, 2rem);
  padding-top: 6rem;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 5rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  .back-btn {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 0.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    &:hover { background: #f9fafb; border-color: #d1d5db; }
  }

  .title-area {
    h1 { font-size: 1.5rem; font-weight: 800; color: #111827; margin: 0; }
    p { color: #6b7280; font-size: 0.9rem; margin: 0.25rem 0 0; }
  }
`;


const EmptyState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 16px;
  border: 1px dashed #d1d5db;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 4rem;
  color: #6b7280;
`;

const TeacherPublicReviews: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<any[]>([]);
  const [teacher, setTeacher] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch teacher details
        const teacherResp = await apiService.get<any>(API_ENDPOINTS.USERS.GET(teacherId!));
        setTeacher(teacherResp);

        // Fetch teacher reviews (completed lessons with ratings)
        // We use the existing teaching lessons endpoint but filtered for this teacher
        const reviewsResp = await apiService.get<any>(`/api/lessons/teacher/${teacherResp.username}?filter=completed`);
        const data = Array.isArray(reviewsResp?.data) ? reviewsResp.data : Array.isArray(reviewsResp) ? reviewsResp : [];
        
        const filteredReviews = data
          .filter((l: any) => l.studentRating != null)
          .sort((a: any, b: any) => new Date(b.lessonDateTime || b.dateTime).getTime() - new Date(a.lessonDateTime || a.dateTime).getTime());
        
        setReviews(filteredReviews);
      } catch (err: any) {
        setError(err.message || 'Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };

    if (teacherId) loadData();
  }, [teacherId]);

  if (loading) return (
    <PageWrapper>
      <ContentWrapper>
        <LoadingSpinner>{t('common.loading', 'Loading reviews...')}</LoadingSpinner>
      </ContentWrapper>
    </PageWrapper>
  );

  return (
    <PageWrapper>
      <ContentWrapper>
        <Header>
          <button className="back-btn" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </button>
          <div className="title-area">
            <h1>{teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Teacher'} - {t('portal.teacher.reviews.title', 'Reviews')}</h1>
            <p>{reviews.length} {t('portal.teacher.reviews.count_label', 'received reviews')}</p>
          </div>
        </Header>

        {error && <EmptyState style={{ border: '1px solid #fee2e2', color: '#ef4444', background: '#fef2f2' }}>{error}</EmptyState>}

        {!error && reviews.length === 0 ? (
          <EmptyState>{t('portal.teacher.reviews.none', 'No reviews yet for this instructor.')}</EmptyState>
        ) : (
          reviews.map((review, idx) => (
            <ReviewCard 
              key={review.id}
              review={review}
              idx={idx}
              isExpanded={true}
            />
          ))
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherPublicReviews;
